import { useLocation, useNavigate } from "react-router-dom";
import { Bot, ChevronDown, Folder, Notebook, User, Users } from "lucide-react";
import { ManifoldMonoBadgeIcon } from "~/assets/icons";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import useUserPermission from "~/hooks/useUserPermission";

const BASE_NAV_SECTIONS = [
  {
    label: "Projects",
    description: "Organize research projects",
    href: "/nextgen/projects",
    Icon: Notebook,
  },
  {
    label: "Datasets",
    description: "Explore datasets and files",
    href: "/nextgen/datasets",
    Icon: Folder,
  },
  {
    label: "Agents",
    description: "Manage AI-powered research assistants",
    href: "/nextgen/agents",
    Icon: Bot,
  },
];

const ADMIN_NAV_SECTIONS = [
  {
    label: "Users",
    description: "Manage user accounts and permissions",
    href: "/nextgen/admin/users",
    Icon: User,
  },
  {
    label: "Groups",
    description: "Manage user groups and access",
    href: "/nextgen/admin/groups",
    Icon: Users,
  },
];

export function ManifoldNextGenNavPicker() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const canViewAdmin = useUserPermission("view:admin-app");

  const navSections = [...BASE_NAV_SECTIONS, ...(canViewAdmin ? ADMIN_NAV_SECTIONS : [])];

  const activeSection = navSections.find((s) => pathname.startsWith(s.href));
  const sectionLabel = activeSection?.label ?? "";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium text-foreground focus-visible:ring-0 dark:bg-[var(--dt-primitives-color-neutral-800)] dark:shadow-none dark:hover:bg-[var(--dt-primitives-color-neutral-900)]"
          style={{ boxShadow: "var(--atlas-shadow-sm)" }}
        >
          <ManifoldMonoBadgeIcon className="size-5 shrink-0" />
          {sectionLabel && <span>{sectionLabel}</span>}
          <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="flex flex-col p-1.5">
        {navSections.map(({ label, description, href, Icon }) => (
          <DropdownMenuItem
            key={href}
            onClick={() => navigate(href)}
            className="group flex cursor-pointer items-start gap-3 rounded-lg px-3 py-2.5 transition-colors duration-150 focus:bg-primary/10 focus:text-foreground dark:focus:bg-primary/20"
          >
            <Icon className="size-5 shrink-0 text-muted-foreground transition-colors duration-150 group-focus:text-primary" />
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-semibold leading-snug text-foreground">{label}</span>
              <span className="text-xs leading-snug text-muted-foreground">{description}</span>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
